'use client'

import { useEffect, useState } from 'react'
import { useKeyboardInset } from '@/lib/hooks/use-keyboard-inset'

/**
 * True when the on-screen keyboard is likely open (mobile / native WebView).
 * Uses the shared keyboard inset store (Capacitor events or visualViewport).
 *
 * Always returns `false` until after first client mount. The underlying inset
 * store is a module-level singleton that survives client-side navigation —
 * if a user enters a page with the keyboard already open (because it was open
 * on the previous page), the first render reads a stale non-zero inset while
 * the SSR HTML was built with `0`. That mismatch trips React #418 in any
 * consumer that gates a className on this value, and the recovery render
 * can cascade into #310 in larger components. Deferring until mount keeps
 * SSR and first CSR render identical; the real keyboard state then takes
 * effect on the second render, masked by any CSS transition the consumer
 * already has on the affected styles. PostHog issues 019dda94 + 019df60f.
 */
export function useKeyboardVisible(): boolean {
  const isOpen = useKeyboardInset().isKeyboardOpen
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true)
  }, [])
  return mounted && isOpen
}
